import type { Estate, Host, HostDisk } from '@/db/api'
import { CRITICAL_AT, FULL_AT, pctOf, shareOf } from './storage'

/**
 * The infra pack's Hosts (components/Hosts.tsx, /infra/hosts), pure: what
 * needs a look on a machine, its words, and the machines grouped by estate.
 * internal/hosts reports facts only; the lines are here. Disks use Storage's
 * (85% a warning, 95% critical) so a disk reads the same on both.
 */

/** Memory in use, CPU busy and load per core past which a host needs a look. */
export const MEMORY_HIGH = 0.9
export const CPU_HIGH = 0.9
export const LOAD_HIGH = 1.5

export type Level = 'ok' | 'warn' | 'critical'

export interface Issue {
  severity: 'critical' | 'warning'
  text: string
}

/** A disk's level by how full it is. */
export const diskLevel = (d: HostDisk): Level => {
  const s = shareOf(d)
  return s >= CRITICAL_AT ? 'critical' : s >= FULL_AT ? 'warn' : 'ok'
}

/** A 0..1 figure's level against its line (CPU, memory). */
export const shareLevel = (v: number | undefined, high: number): Level =>
  v !== undefined && v >= high ? 'warn' : 'ok'

/**
 * What needs a look on a host, critical first: not reporting, a drive failing
 * SMART or a disk past 95% (critical); a disk past 85%, memory past 90%, CPU
 * past 90% or load past 1.5 per core (warning). A waiting reboot is shown, not
 * judged: it is routine after an upgrade.
 */
export const hostIssues = (h: Host): Issue[] => {
  if (!h.up) return [{ severity: 'critical', text: `${h.name} is not reporting` }]
  const out: Issue[] = []
  if (h.smart === 'failing')
    out.push({
      severity: 'critical',
      text: `${h.name}: ${(h.smart_failing ?? []).join(', ') || 'a drive'} fails its SMART check`,
    })
  if (h.worst) {
    const level = diskLevel(h.worst)
    if (level !== 'ok')
      out.push({
        severity: level === 'critical' ? 'critical' : 'warning',
        text: `${h.name} ${h.worst.mount} is ${pctOf(shareOf(h.worst))}% full`,
      })
  }
  if (h.memory !== undefined && h.memory >= MEMORY_HIGH)
    out.push({ severity: 'warning', text: `${h.name} memory is ${pct(h.memory)}% used` })
  if (h.cpu !== undefined && h.cpu >= CPU_HIGH)
    out.push({ severity: 'warning', text: `${h.name} CPU is ${pct(h.cpu)}% busy` })
  if (h.load !== undefined && h.load >= LOAD_HIGH)
    out.push({ severity: 'warning', text: `${h.name} load is ${fmtLoad(h.load)} per core` })
  return out.sort((a, b) => rank(a) - rank(b))
}

const rank = (i: Issue) => (i.severity === 'critical' ? 0 : 1)

/** A host's level: its worst issue. */
export const hostLevel = (h: Host): Level => {
  const issues = hostIssues(h)
  return issues.some((i) => i.severity === 'critical') ? 'critical' : issues.length ? 'warn' : 'ok'
}

/** Whole percent, rounded (a disk's is rounded down, as Storage's: `pctOf`). */
export const pct = (share: number): number => Math.round(share * 100)

export const fmtLoad = (v: number): string => v.toFixed(2)

/** Time since boot in one unit: 12 min, 7 h, 3 days, 245 days. */
export const fmtUptime = (seconds: number): string => {
  if (seconds < 3600) return `${Math.max(1, Math.floor(seconds / 60))} min`
  if (seconds < 2 * 86400) return `${Math.floor(seconds / 3600)} h`
  return `${Math.floor(seconds / 86400)} days`
}

const ROLES: Record<string, string> = {
  nas: 'NAS',
  hypervisor: 'Hypervisor',
  'control-plane': 'Control plane',
  media: 'Media node',
  node: 'Cluster node',
}

export const roleWords = (role: string): string => ROLES[role] ?? 'Host'

export const ESTATE_NAMES: Record<Estate, string> = {
  hetzner: 'Hetzner',
  home: 'Home',
  oracle: 'Oracle Cloud',
  other: 'Elsewhere',
}

const ESTATE_ORDER: Estate[] = ['hetzner', 'home', 'oracle', 'other']

/** The hosts by estate, in the server's order within each; an estate with none is left out. */
export const byEstate = (hosts: Host[]): { estate: Estate; name: string; hosts: Host[] }[] =>
  ESTATE_ORDER.map((estate) => ({
    estate,
    name: ESTATE_NAMES[estate],
    hosts: hosts.filter((h) => h.estate === estate),
  })).filter((g) => g.hosts.length > 0)

/** The hosts that need a look, worst first (critical, then warning), each with its worst issue. */
export const hostsToLook = (hosts: Host[]): { host: Host; issue: Issue; level: Level }[] =>
  hosts
    .flatMap((host) => {
      const issues = hostIssues(host)
      return issues.length ? [{ host, issue: issues[0]!, level: hostLevel(host) }] : []
    })
    .sort((a, b) => rank(a.issue) - rank(b.issue))
